import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaEye, FaEyeSlash, FaLock, FaStore } from "react-icons/fa";
import { useI18n } from "../../contexts/I18n";
import { marketingI18n } from "../../content/homeMarketing";
import "../AuthForms.css";
import "./ShopOwnerLogin.css";

const PUB = process.env.PUBLIC_URL || "";
const WELCOME_VIDEO_SRC = `${PUB}/videos/welcome.mp4`;
const HERO_BRAND_LOGO = `${PUB}/images/ehgezly-hero-logo.png?v=4`;

export default function LoginView({ generatedAccount, logErr, cred, onCred, doLogin }) {
  const { t, lang } = useI18n();
  const pageT = marketingI18n[lang].pages.ownerLogin;
  const isRtl = lang === "ar";
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      el.pause();
      return undefined;
    }
    el.muted = true;
    const play = () => {
      const p = el.play();
      if (p?.catch) p.catch(() => {});
    };
    play();
    el.addEventListener("loadeddata", play);
    return () => el.removeEventListener("loadeddata", play);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await doLogin(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`auth-page auth-page--split auth-page--owner${isRtl ? " rtl" : ""}`}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <aside className="auth-brand auth-brand--showcase" aria-label={t("ownerLoginTitle")}>
        <div className="auth-brand__video" aria-hidden="true">
          <video
            ref={videoRef}
            className="auth-brand__video-el"
            src={WELCOME_VIDEO_SRC}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
          <div className="auth-brand__video-veil" />
        </div>
        <div className="auth-brand__hero-mark">
          <div className="auth-brand__hero-logo-wrap">
            <img
              src={HERO_BRAND_LOGO}
              alt=""
              className="auth-brand__hero-logo"
              draggable="false"
            />
          </div>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-form-panel">
          <header className="auth-form-head">
            <h2>{t("ownerLoginTitle")}</h2>
            <p>{t("ownerLoginSubtitle")}</p>
          </header>

          {generatedAccount && (
            <div className="auth-owner-creds" role="status">
              <p className="auth-owner-creds__label">{pageT.credsTitle}</p>
              <p className="auth-owner-creds__value">
                <strong>{generatedAccount.shopName}</strong>
                <span> · </span>
                {generatedAccount.password}
              </p>
              <p className="auth-owner-creds__hint">{pageT.credsHint}</p>
            </div>
          )}

          {logErr && (
            <div className="error-message" role="alert">
              {logErr}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit} noValidate aria-busy={isLoading}>
            <div className="form-group">
              <label className="form-label form-label-with-icon" htmlFor="owner-shop-name">
                <span className="form-label-icon" aria-hidden="true">
                  <FaStore className="form-label-icon-svg" />
                </span>
                <span className="form-label-text">{t("ownerLoginShop")}</span>
              </label>
              <input
                id="owner-shop-name"
                name="shopName"
                type="text"
                placeholder={t("ownerLoginShop")}
                value={cred.shopName}
                onChange={onCred}
                required
                disabled={isLoading}
                autoComplete="username"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label form-label-with-icon" htmlFor="owner-password">
                <span className="form-label-icon" aria-hidden="true">
                  <FaLock className="form-label-icon-svg" />
                </span>
                <span className="form-label-text">{t("ownerLoginPassword")}</span>
              </label>
              <div className="auth-password-field">
                <input
                  id="owner-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("ownerLoginPassword")}
                  value={cred.password}
                  onChange={onCred}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  className="form-input"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((p) => !p)}
                  disabled={isLoading}
                  aria-pressed={showPassword}
                  aria-label={
                    showPassword
                      ? lang === "ar"
                        ? "إخفاء"
                        : "Hide"
                      : lang === "ar"
                        ? "إظهار"
                        : "Show"
                  }
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <button type="submit" className="auth-button" disabled={isLoading}>
              <span className="button-content">
                {isLoading ? pageT.signingIn : t("ownerLoginBtn")}
              </span>
            </button>
          </form>

          <div className="auth-footer">
            <p>
              {t("ownerNeedHelp")}
              <Link to="/support" className="auth-link">
                {t("ownerContactSupport")}
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
